import { useCallback, useEffect, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ArrowBackIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { getBeneficiaryDetails } from 'api/beneficiaries';
import { useAuthentication } from 'AuthContext';
import { PermissionsV2 } from 'constants/permissions_v2';
import { BENEFICIARIES_ROUTE, SUPER_MERCHANT_BENEFICIARIES_ROUTE } from 'constants/routes';
import UpdateBeneficiaryStatusModal from 'modules/beneficiaries/components/UpdateBeneficiaryStatusModal';
import UploadBeneficiaryDocumentModal from 'modules/beneficiaries/components/UploadBeneficiaryDocumentModal';
import { BENEFICIARY_DETAIL_SECTIONS } from 'modules/beneficiaries/constants/constants';
import { formatBeneficiaryStatus, getBeneficiaryStatusBadgeColorScheme } from 'modules/beneficiaries/utils/utils';
import { formatDateTime } from 'utils/utils';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

const BeneficiaryDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { beneficiaryId } = useParams();
  const { userPermissions } = useAuthentication();

  const [beneficiary, setBeneficiary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const uploadDocumentModal = useDisclosure();
  const updateStatusModal = useDisclosure();

  const canManageBeneficiary = userPermissions?.has(PermissionsV2.MANAGE_WHITELIST_RESOURCES_OPERATIONS);

  const fetchBeneficiary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBeneficiaryDetails(beneficiaryId);

      setBeneficiary(response?.data?.data ?? null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [beneficiaryId]);

  useEffect(() => {
    fetchBeneficiary();
  }, [fetchBeneficiary]);

  const handleBack = () => {
    const superMerchantId = location?.state?.superMerchantId ?? beneficiary?.super_merchant_id;
    const tab = location?.state?.tab ?? 0;

    if (superMerchantId) {
      history.push(`${SUPER_MERCHANT_BENEFICIARIES_ROUTE(superMerchantId)}?tab=${tab}`);

      return;
    }

    history.push(BENEFICIARIES_ROUTE);
  };

  return (
    <Box p={4}>
      <Flex align='center' justify='space-between' mb={4}>
        <Flex align='center' gap={3}>
          <IconButton icon={<ArrowBackIcon />} size='sm' variant='outline' aria-label='Back' onClick={handleBack} />
          <Box>
            <Heading as='h1' size='md' color='gray.700'>
              {beneficiary?.beneficiary_name || 'Beneficiary'}
            </Heading>
            <Text fontSize='sm' color='gray.500'>
              {beneficiaryId}
            </Text>
          </Box>
          {!!beneficiary?.status && (
            <Badge colorScheme={getBeneficiaryStatusBadgeColorScheme(beneficiary?.status)} rounded='full' px={3}>
              {formatBeneficiaryStatus(beneficiary?.status)}
            </Badge>
          )}
        </Flex>

        {canManageBeneficiary && !!beneficiary && (
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<BsThreeDotsVertical />}
              variant='ghost'
              aria-label='More options'
              size='sm'
            />
            <MenuList>
              <MenuItem onClick={uploadDocumentModal.onOpen}>Upload Document</MenuItem>
              <MenuItem onClick={updateStatusModal.onOpen}>Update Status</MenuItem>
            </MenuList>
          </Menu>
        )}
      </Flex>

      <LoadingErrorWrapper isLoading={loading} errorTitle='Error fetching beneficiary details' error={error}>
        {!beneficiary ? (
          <Box textAlign='center' mt={4}>
            <Heading size='md' color='gray.500'>
              Beneficiary not found
            </Heading>
          </Box>
        ) : (
          <>
            {BENEFICIARY_DETAIL_SECTIONS.map((section) => (
              <Box key={section.title} p={4} mb={4} borderRadius='lg' boxShadow='sm' bg='white'>
                <Heading as='h2' size='sm' mb={4} color='gray.700'>
                  {section.title}
                </Heading>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                  {section.fields.map((field) => (
                    <GridItem key={field.key}>
                      <Text fontSize='xs' color='gray.500' textTransform='uppercase'>
                        {field.label}
                      </Text>
                      <Text fontSize='sm' color='gray.700' wordBreak='break-word'>
                        {beneficiary?.[field.key] || '-'}
                      </Text>
                    </GridItem>
                  ))}
                </Grid>
              </Box>
            ))}

            <Box p={4} mb={4} borderRadius='lg' boxShadow='sm' bg='white'>
              <Heading as='h2' size='sm' mb={4} color='gray.700'>
                Timestamps
              </Heading>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                <GridItem>
                  <Text fontSize='xs' color='gray.500' textTransform='uppercase'>
                    Created At
                  </Text>
                  <Text fontSize='sm' color='gray.700'>
                    {beneficiary?.created_at ? formatDateTime(beneficiary.created_at) : '-'}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text fontSize='xs' color='gray.500' textTransform='uppercase'>
                    Updated At
                  </Text>
                  <Text fontSize='sm' color='gray.700'>
                    {beneficiary?.updated_at ? formatDateTime(beneficiary.updated_at) : '-'}
                  </Text>
                </GridItem>
              </Grid>
            </Box>

            <Box p={4} borderRadius='lg' boxShadow='sm' bg='white'>
              <Heading as='h2' size='sm' mb={4} color='gray.700'>
                Documents
              </Heading>

              {!!beneficiary?.documents && (
                <Box mb={4}>
                  <Text fontSize='xs' color='gray.500' textTransform='uppercase'>
                    Latest Document
                  </Text>
                  {beneficiary?.documents_url ? (
                    <Link
                      fontSize='sm'
                      color='blue.600'
                      href={beneficiary.documents_url}
                      isExternal
                      wordBreak='break-all'
                    >
                      {beneficiary.documents}
                    </Link>
                  ) : (
                    <Text fontSize='sm' color='gray.700' wordBreak='break-all'>
                      {beneficiary.documents}
                    </Text>
                  )}
                </Box>
              )}

              {!beneficiary?.document_mappings?.length ? (
                <Text fontSize='sm' color='gray.500'>
                  No documents uploaded for this beneficiary
                </Text>
              ) : (
                <TableContainer borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto'>
                  <Table variant='simple' size='sm'>
                    <Thead bg='gray.100'>
                      <Tr>
                        <Th>Uploaded At</Th>
                        <Th>Document Type</Th>
                        <Th>Document Name</Th>
                        <Th>Document</Th>
                        <Th>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {beneficiary.document_mappings.map((document) => (
                        <Tr key={document?.id} fontSize='sm'>
                          <Td color='gray.600'>{document?.created_at ? formatDateTime(document.created_at) : '-'}</Td>
                          <Td color='gray.600'>{document?.document_type || '-'}</Td>
                          <Td color='gray.600'>{document?.document_name || '-'}</Td>
                          <Td>
                            {document?.document_url ? (
                              <Link color='blue.600' href={document.document_url} isExternal wordBreak='break-all'>
                                Open
                              </Link>
                            ) : (
                              '-'
                            )}
                          </Td>
                          <Td color='gray.600'>{formatBeneficiaryStatus(document?.status)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </LoadingErrorWrapper>

      <UploadBeneficiaryDocumentModal
        isOpen={uploadDocumentModal.isOpen}
        onClose={uploadDocumentModal.onClose}
        beneficiary={beneficiary}
        onUploaded={fetchBeneficiary}
      />
      <UpdateBeneficiaryStatusModal
        isOpen={updateStatusModal.isOpen}
        onClose={updateStatusModal.onClose}
        beneficiary={beneficiary}
        onUpdated={fetchBeneficiary}
      />
    </Box>
  );
};

export default BeneficiaryDetails;
